// BaseEntitlementsService.js

import { functions, httpsCallable } from 'firebaseConfig';

import { PLAN_PRICING_MODEL } from 'services/plans/PlanService';
import { buildTenantCallableFields } from 'utilities/basePersistence';

const callable = (name) => httpsCallable(functions, name);

const entitlementsCache = new Map();

function normalizeNonNegativeInteger(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  const whole = Math.floor(parsed);
  return whole < 0 ? 0 : whole;
}

function normalizeNullablePositiveInteger(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  const whole = Math.floor(parsed);
  return whole < 1 ? null : whole;
}

function resolvePerSeatField(plan, primaryKey, legacyKey) {
  const primary = normalizeNullablePositiveInteger(plan?.[primaryKey]);
  if (primary != null) {
    return primary;
  }

  return normalizeNullablePositiveInteger(plan?.[legacyKey]);
}

export function computeBilledSeatCount({
  memberCount = 0,
  pendingInviteCount = 0,
} = {}) {
  const members = Math.max(0, Math.floor(Number(memberCount || 0)));
  const invites = Math.max(0, Math.floor(Number(pendingInviteCount || 0)));
  const total = members + invites;
  return Math.max(1, total);
}

export function computeEffectiveLimitsFromPlan({
  billedSeatCount = 1,
  plan = null,
} = {}) {
  const seats = Math.max(1, Math.floor(Number(billedSeatCount || 1)));
  const storageBytesPerSeat = resolvePerSeatField(plan, 'storageBytesPerSeat', 'pooledStorageBytes');
  const apiMonthlyRunLimitPerSeat = normalizeNonNegativeInteger(
    resolvePerSeatField(plan, 'apiMonthlyRunLimitPerSeat', 'apiMonthlyRunLimit'),
    0
  );
  const liveParticipantMinutesPerSeat = normalizeNonNegativeInteger(
    resolvePerSeatField(plan, 'liveParticipantMinutesPerSeat', 'liveParticipantMinutesMonthlyCap'),
    0
  );
  const liveEgressMinutesPerSeat = normalizeNonNegativeInteger(
    resolvePerSeatField(plan, 'liveEgressMinutesPerSeat', 'liveEgressMinutesMonthlyCap'),
    0
  );

  return {
    apiMonthlyRunLimit: apiMonthlyRunLimitPerSeat > 0 ? apiMonthlyRunLimitPerSeat * seats : 0,
    billedSeatCount: seats,
    features: {
      apiMonthlyRunLimitPerSeat,
      liveEgressMinutesPerSeat,
      liveParticipantMinutesPerSeat,
      maxUploadBytes: normalizeNullablePositiveInteger(plan?.maxUploadBytes),
      storageBytesPerSeat,
    },
    limits: {
      apiMonthlyRunLimit: apiMonthlyRunLimitPerSeat > 0 ? apiMonthlyRunLimitPerSeat * seats : 0,
      liveEgressMinutesMonthlyCap: liveEgressMinutesPerSeat > 0
        ? liveEgressMinutesPerSeat * seats
        : 0,
      liveParticipantMinutesMonthlyCap: liveParticipantMinutesPerSeat > 0
        ? liveParticipantMinutesPerSeat * seats
        : 0,
      storageBytes: storageBytesPerSeat != null ? storageBytesPerSeat * seats : null,
    },
  };
}

export function resolveMemberCapFromPlan(plan) {
  if (!plan) return null;
  if (String(plan.pricingModel || '').trim() === PLAN_PRICING_MODEL.PER_SEAT) {
    return null;
  }

  return normalizeNullablePositiveInteger(plan.maxUsers);
}

export async function getBaseEntitlements({ baseKey }) {
  const cleanedBaseKey = String(baseKey || '').trim();
  if (!cleanedBaseKey) {
    throw new Error('baseKey is required');
  }

  if (!entitlementsCache.has(cleanedBaseKey)) {
    const requestPromise = callable('getBaseEntitlements')({ ...buildTenantCallableFields(cleanedBaseKey) })
      .then((result) => result?.data || null)
      .catch((error) => {
        entitlementsCache.delete(cleanedBaseKey);
        throw error;
      });

    entitlementsCache.set(cleanedBaseKey, requestPromise);
  }

  return entitlementsCache.get(cleanedBaseKey);
}

export function clearBaseEntitlementsCache(baseKey = null) {
  const cleanedBaseKey = String(baseKey || '').trim();
  if (!cleanedBaseKey) {
    entitlementsCache.clear();
    return;
  }

  entitlementsCache.delete(cleanedBaseKey);
}
